/* Stores page and store pages (Oct 2026). Open status is worked out in each store's own
   time zone from the hours the nightly sync copies out of Google, so a Perth store reads
   correctly to someone browsing in Sydney. No dependencies. */
(() => {
  const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const DOW_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const CAL = '<svg class="ms-ic" viewBox="0 0 256 256" aria-hidden="true"><path d="M208,32H184V24a8,8,0,0,0-16,0v8H88V24a8,8,0,0,0-16,0v8H48A16,16,0,0,0,32,48V208a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V48A16,16,0,0,0,208,32ZM72,48v8a8,8,0,0,0,16,0V48h80v8a8,8,0,0,0,16,0V48h24V80H48V48ZM208,208H48V96H208V208Z"/></svg>';

  const parse = (s, fallback) => { try { return JSON.parse(s) ?? fallback; } catch { return fallback; } };

  function nowIn(tz) {
    let parts;
    try {
      parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "short", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date());
    } catch {
      parts = new Intl.DateTimeFormat("en-US", { weekday: "short", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date());
    }
    const g = (t) => parts.find((p) => p.type === t)?.value;
    return { day: DOW.indexOf(g("weekday")), mins: (+g("hour") % 24) * 60 + +g("minute"), date: `${g("year")}-${g("month")}-${g("day")}` };
  }

  function addDays(iso, n) {
    const d = new Date(`${iso}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
  }

  function fmt(mins) {
    const m = ((mins % 1440) + 1440) % 1440;
    const h = Math.floor(m / 60), mm = m % 60;
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}${mm ? `:${String(mm).padStart(2, "0")}` : ""}${h < 12 ? "am" : "pm"}`;
  }

  function storeData(el) {
    return {
      tz: el.dataset.tz || "Australia/Sydney",
      status: el.dataset.status || "open",
      periods: parse(el.dataset.periods, []) || [],
      special: parse(el.dataset.special, []) || [],
    };
  }

  /** [open, close] minute ranges for one calendar date, holiday hours first. */
  function dayPeriods(s, date, dow) {
    const sp = s.special.find((x) => x.date === date && x.confirmed);
    if (sp) return (sp.periods || []).map(([o, c]) => [o, c]);
    return s.periods.filter((p) => p[0] === dow).map(([od, om, cd, cm]) => [om, ((cd - od + 7) % 7) * 1440 + cm]);
  }

  function statusOf(s) {
    if (s.status === "coming_soon") return { cls: "is-soon", text: "Opening soon" };
    if (s.status === "temporarily_closed") return { cls: "is-closed", text: "Temporarily closed" };
    if (s.status === "closed") return { cls: "is-closed", text: "Closed" };
    if (!s.periods.length) return { cls: "is-closed", text: "Hours not listed" };
    const n = nowIn(s.tz);
    const open = (close) => ({ cls: "is-open", text: close - n.mins >= 1440 ? "Open 24 hours" : `Open now · closes ${fmt(close)}` });
    for (const [, c] of dayPeriods(s, addDays(n.date, -1), (n.day + 6) % 7)) if (c > 1440 && n.mins < c - 1440) return open(c - 1440 + 0);
    for (const [o, c] of dayPeriods(s, n.date, n.day)) if (n.mins >= o && n.mins < c) return open(c);
    for (let i = 0; i < 8; i++) {
      const dow = (n.day + i) % 7;
      const next = dayPeriods(s, addDays(n.date, i), dow).filter(([o]) => i > 0 || o > n.mins).sort((a, b) => a[0] - b[0])[0];
      if (next) return { cls: "is-closed", text: i === 0 ? `Opens ${fmt(next[0])}` : i === 1 ? `Closed · opens tomorrow ${fmt(next[0])}` : `Closed · opens ${DOW_LONG[dow]} ${fmt(next[0])}` };
    }
    return { cls: "is-closed", text: "Closed" };
  }

  /** The next public holiday or special-hours day this week, if any. */
  function upcomingSpecial(s) {
    const today = nowIn(s.tz).date;
    const until = addDays(today, 6);
    return s.special.filter((x) => x.date >= today && x.date <= until && (x.holiday || x.confirmed)).sort((a, b) => a.date.localeCompare(b.date))[0];
  }

  function renderStatus(el) {
    const s = storeData(el);
    const out = el.querySelector("[data-ms-status]");
    if (out) {
      const st = statusOf(s);
      out.className = `ms-status ${st.cls}`;
      out.innerHTML = `<i aria-hidden="true"></i>${st.text}`;
    }
    const flag = el.querySelector("[data-ms-hol]");
    if (flag && s.status === "open") {
      const sp = upcomingSpecial(s);
      if (sp) {
        const when = sp.date === nowIn(s.tz).date ? "today" : DOW[new Date(`${sp.date}T12:00:00Z`).getUTCDay()];
        const hours = sp.confirmed ? sp.text : "Hours not confirmed";
        flag.innerHTML = `${CAL}<span class="ms-hol__text"><b>${sp.name || "Special hours"}, ${when}</b><span>${hours}</span></span>`;
        flag.classList.toggle("ms-hol--soft", !sp.confirmed);
        flag.hidden = false;
      }
    }
  }

  function km(a, b) {
    const R = 6371, r = (x) => (x * Math.PI) / 180;
    const h = Math.sin(r(b.lat - a.lat) / 2) ** 2 + Math.cos(r(a.lat)) * Math.cos(r(b.lat)) * Math.sin(r(b.lng - a.lng) / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  }
  const kmText = (d) => (d < 10 ? `${d.toFixed(1)} km away` : `${Math.round(d).toLocaleString("en-AU")} km away`);
  const norm = (s) => (s || "").toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "");

  /* ---------- Stores page ---------- */
  function initIndex(root) {
    const cards = [...root.querySelectorAll("[data-ms-store]")];
    cards.forEach(renderStatus);
    const regions = [...root.querySelectorAll("[data-ms-region]")];
    const pills = [...root.querySelectorAll("[data-ms-filter]")];
    const search = root.querySelector("[data-ms-search]");
    const nearBtn = root.querySelector("[data-ms-near]");
    const nearest = root.querySelector("[data-ms-nearest]");
    const nearestGrid = nearest?.querySelector(".ms-grid");
    const empty = root.querySelector("[data-ms-empty]");
    const note = root.querySelector("[data-ms-note]");
    const state = { region: "all", q: "", here: null };

    function apply() {
      const tokens = norm(state.q).split(/\s+/).filter(Boolean);
      const matches = (c) => (state.region === "all" || c.dataset.region === state.region) && tokens.every((t) => norm(c.dataset.search).includes(t));
      let shown = 0;
      if (state.here && nearestGrid) {
        regions.forEach((r) => (r.hidden = true));
        nearestGrid.replaceChildren();
        cards.filter(matches).map((c) => ({ c, d: km(state.here, { lat: +c.dataset.lat, lng: +c.dataset.lng }) }))
          .filter((x) => Number.isFinite(x.d)).sort((a, b) => a.d - b.d).slice(0, 12)
          .forEach(({ c, d }) => {
            const clone = c.cloneNode(true);
            const dist = clone.querySelector("[data-ms-distance]");
            if (dist) { dist.textContent = kmText(d); dist.hidden = false; }
            nearestGrid.append(clone);
            shown++;
          });
        nearest.hidden = shown === 0;
      } else {
        if (nearest) nearest.hidden = true;
        regions.forEach((r) => {
          let n = 0;
          r.querySelectorAll("[data-ms-store]").forEach((c) => { const ok = matches(c); c.hidden = !ok; if (ok) n++; });
          r.hidden = n === 0;
          shown += n;
        });
      }
      if (empty) {
        empty.hidden = shown > 0;
        const q = empty.querySelector("[data-ms-empty-q]");
        if (q) q.textContent = state.q.trim();
      }
    }

    pills.forEach((p) => p.addEventListener("click", () => {
      state.region = p.dataset.msFilter;
      pills.forEach((x) => x.setAttribute("aria-pressed", String(x === p)));
      apply();
    }));
    let t;
    search?.addEventListener("input", () => { clearTimeout(t); t = setTimeout(() => { state.q = search.value; apply(); }, 120); });

    nearBtn?.addEventListener("click", () => {
      if (state.here) {
        state.here = null;
        nearBtn.setAttribute("aria-pressed", "false");
        if (note) note.hidden = true;
        return apply();
      }
      if (!navigator.geolocation) return;
      nearBtn.disabled = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          nearBtn.disabled = false;
          state.here = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          nearBtn.setAttribute("aria-pressed", "true");
          if (note) note.hidden = true;
          apply();
          nearest?.scrollIntoView({ behavior: "smooth", block: "start" });
        },
        () => {
          nearBtn.disabled = false;
          if (note) { note.textContent = "We couldn't get your location. Search by suburb or postcode instead."; note.hidden = false; }
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 },
      );
    });
  }

  /* ---------- Suggestion form ---------- */
  const NAME = /^[\p{L}\p{M}][\p{L}\p{M}' .\-’]{0,39}$/u;
  const EMAIL = /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/i;
  const DOMAINS = ["gmail.com", "hotmail.com", "outlook.com", "yahoo.com", "yahoo.com.au", "icloud.com", "me.com", "live.com", "live.com.au", "bigpond.com", "bigpond.net.au", "optusnet.com.au", "hotmail.com.au", "outlook.com.au", "protonmail.com"];

  function lev(a, b) {
    const d = Array.from({ length: a.length + 1 }, (_, i) => [i]);
    for (let j = 1; j <= b.length; j++) d[0][j] = j;
    for (let i = 1; i <= a.length; i++) for (let j = 1; j <= b.length; j++) d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    return d[a.length][b.length];
  }
  function typoFix(email) {
    const [user, domain] = email.toLowerCase().split("@");
    if (!domain || DOMAINS.includes(domain)) return null;
    const best = DOMAINS.map((d) => [d, lev(domain, d)]).sort((a, b) => a[1] - b[1])[0];
    return best && best[1] > 0 && best[1] <= 2 ? `${user}@${best[0]}` : null;
  }
  const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`);

  function initSuggest(form) {
    const endpoint = form.dataset.endpoint;
    const f = (n) => form.elements[n];
    const sub = f("suburb");
    const placeId = f("place_id");
    const list = form.querySelector("[data-ms-list]");
    const btn = form.querySelector("[type=submit]");
    const formError = form.querySelector("[data-ms-form-error]");
    let session = uuid(), options = [], active = -1, timer, req = 0;

    const errEl = (name) => form.querySelector(`[data-ms-error="${name}"]`);
    function setError(name, msg, html) {
      const el = errEl(name), input = f(name === "place" ? "suburb" : name);
      if (el) { if (html) el.innerHTML = html; else el.textContent = msg || ""; el.hidden = !msg && !html; }
      if (input) input.setAttribute("aria-invalid", msg || html ? "true" : "false");
    }

    function close() { list.hidden = true; sub.setAttribute("aria-expanded", "false"); sub.removeAttribute("aria-activedescendant"); active = -1; }
    function highlight(i) {
      active = i;
      [...list.querySelectorAll("[role=option]")].forEach((li, k) => li.setAttribute("aria-selected", String(k === i)));
      if (i >= 0) sub.setAttribute("aria-activedescendant", `ms-opt-${i}`);
    }
    function choose(i) {
      const o = options[i];
      if (!o) return;
      sub.value = o.secondary ? `${o.main}, ${o.secondary}` : o.main;
      placeId.value = o.id;
      setError("place", "");
      close();
    }
    function render() {
      list.replaceChildren();
      if (!options.length) {
        list.insertAdjacentHTML("beforeend", '<li class="ms-list__empty">No matches yet. Try the suburb or town name.</li>');
      }
      options.forEach((o, i) => {
        const li = document.createElement("li");
        li.id = `ms-opt-${i}`;
        li.setAttribute("role", "option");
        li.setAttribute("aria-selected", "false");
        li.innerHTML = '<svg class="ms-ic" viewBox="0 0 256 256" aria-hidden="true"><path d="M128,64a40,40,0,1,0,40,40A40,40,0,0,0,128,64Zm0,64a24,24,0,1,1,24-24A24,24,0,0,1,128,128Zm0-112a88.1,88.1,0,0,0-88,88c0,31.4,14.51,64.68,42,96.25a254.19,254.19,0,0,0,41.45,38.3,8,8,0,0,0,9.18,0A254.19,254.19,0,0,0,174,200.25c27.45-31.57,42-64.85,42-96.25A88.1,88.1,0,0,0,128,16Zm0,206c-16.53-13-72-60.75-72-118a72,72,0,0,1,144,0C200,161.23,144.53,209,128,222Z"/></svg>';
        const b = document.createElement("b"); b.textContent = o.main;
        const s = document.createElement("span"); s.textContent = o.secondary;
        li.append(b, s);
        li.addEventListener("mousedown", (e) => { e.preventDefault(); choose(i); });
        list.append(li);
      });
      list.insertAdjacentHTML("beforeend", '<li class="ms-list__credit" aria-hidden="true">Google Maps</li>');
      list.hidden = false;
      sub.setAttribute("aria-expanded", "true");
    }
    async function lookup() {
      const q = sub.value.trim();
      if (q.length < 2) return close();
      const mine = ++req;
      try {
        const r = await fetch(`${endpoint}/places?q=${encodeURIComponent(q)}&s=${encodeURIComponent(session)}`, { headers: { accept: "application/json" } });
        const data = await r.json();
        if (mine !== req) return;
        options = data.suggestions || [];
        render();
      } catch {
        if (mine === req) close();
      }
    }

    sub.addEventListener("input", () => { placeId.value = ""; clearTimeout(timer); timer = setTimeout(lookup, 220); });
    sub.addEventListener("keydown", (e) => {
      if (list.hidden) return;
      if (e.key === "ArrowDown") { e.preventDefault(); highlight(Math.min(active + 1, options.length - 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); highlight(Math.max(active - 1, 0)); }
      else if (e.key === "Enter" && active >= 0) { e.preventDefault(); choose(active); }
      else if (e.key === "Escape") close();
    });
    sub.addEventListener("blur", () => setTimeout(close, 120));

    f("email").addEventListener("blur", () => {
      const v = f("email").value.trim();
      const fix = v && EMAIL.test(v) && typoFix(v);
      if (fix) {
        setError("email", "", `Did you mean <button type="button">${fix.replace(/[<>&"]/g, "")}</button>?`);
        errEl("email").querySelector("button").addEventListener("click", () => { f("email").value = fix; setError("email", ""); });
      }
    });

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      formError.hidden = true;
      const first = f("first_name").value.trim().replace(/\s+/g, " ");
      const email = f("email").value.trim();
      let bad = false;
      if (!first) { setError("first_name", "Add your first name."); bad = true; }
      else if (!NAME.test(first)) { setError("first_name", "Use letters only for your name."); bad = true; }
      else setError("first_name", "");
      if (!email) { setError("email", "Add your email."); bad = true; }
      else if (!EMAIL.test(email)) { setError("email", "That email doesn't look right."); bad = true; }
      if (!placeId.value) { setError("place", sub.value.trim() ? "Pick a suburb from the list." : "Add a suburb or town."); bad = true; }
      if (bad) return form.querySelector('[aria-invalid="true"]')?.focus();

      btn.disabled = true;
      const label = btn.textContent;
      btn.textContent = "Sending…";
      try {
        const r = await fetch(`${endpoint}/suggest`, {
          method: "POST",
          headers: { "content-type": "application/json", accept: "application/json" },
          body: JSON.stringify({ first_name: first, email, place_id: placeId.value, session, company: f("company").value }),
        });
        const data = await r.json().catch(() => ({}));
        if (r.ok && data.ok) {
          const done = form.querySelector("[data-ms-done]");
          done.querySelector("[data-ms-done-title]").textContent = `Thanks, ${data.first_name || first}.`;
          done.querySelector("[data-ms-done-text]").textContent = `${data.label || sub.value} has your vote.`;
          form.querySelector("[data-ms-fields]").hidden = true;
          done.hidden = false;
          done.focus();
          session = uuid();
          return;
        }
        if (data.errors) {
          Object.entries(data.errors).forEach(([k, v]) => setError(k, v));
          form.querySelector('[aria-invalid="true"]')?.focus();
        } else {
          formError.textContent = data.error || "Something went wrong on our end. Try again in a minute.";
          formError.hidden = false;
        }
      } catch {
        formError.textContent = "Couldn't send that. Check your connection and try again.";
        formError.hidden = false;
      } finally {
        btn.disabled = false;
        btn.textContent = label;
      }
    });
  }

  /* ---------- Get notified (coming-soon stores) ---------- */
  /** Sign-up logic shared by the Get notified popup and the store openings page.
      pick() returns the chosen stores as [{ handle, name }]. */
  function bindNotifyForm(form, pick) {
    const endpoint = form.dataset.endpoint;
    const f = (n) => form.elements[n];
    const btn = form.querySelector("[type=submit]");
    const fields = form.querySelector("[data-ms-fields]");
    const done = form.querySelector("[data-ms-done]");
    const formError = form.querySelector("[data-ms-form-error]");

    const errEl = (name) => form.querySelector(`[data-ms-error="${name}"]`);
    function setError(name, msg) {
      const el = errEl(name);
      if (el) { el.textContent = msg || ""; el.hidden = !msg; }
      const input = f(name);
      if (input && input.setAttribute) input.setAttribute("aria-invalid", msg ? "true" : "false");
    }
    function reset() {
      ["first_name", "email", "stores"].forEach((n) => setError(n, ""));
      formError.hidden = true;
      fields.hidden = false;
      done.hidden = true;
    }

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      formError.hidden = true;
      const stores = pick();
      const first = f("first_name").value.trim().replace(/\s+/g, " ");
      const email = f("email").value.trim();
      let bad = false;
      if (!stores.length) { setError("stores", "Pick at least one store."); bad = true; } else setError("stores", "");
      if (!first) { setError("first_name", "Add your first name."); bad = true; }
      else if (!NAME.test(first)) { setError("first_name", "Use letters only for your name."); bad = true; }
      else setError("first_name", "");
      if (!email) { setError("email", "Add your email."); bad = true; }
      else if (!EMAIL.test(email)) { setError("email", "That email doesn't look right."); bad = true; }
      else {
        const fix = typoFix(email);
        if (fix && fix !== email) { setError("email", `Did you mean ${fix}?`); f("email").value = fix; bad = true; }
        else setError("email", "");
      }
      if (bad) return (form.querySelector('[aria-invalid="true"]') || form.querySelector('[data-ms-error]:not([hidden])'))?.focus?.();

      btn.disabled = true;
      const label = btn.textContent;
      btn.textContent = "Signing you up…";
      try {
        const r = await fetch(`${endpoint}/notify`, {
          method: "POST",
          headers: { "content-type": "application/json", accept: "application/json" },
          body: JSON.stringify({ first_name: first, email, stores: stores.map((s) => s.handle), company: f("company").value }),
        });
        const data = await r.json().catch(() => ({}));
        if (r.ok && data.ok) {
          const names = stores.map((s) => s.name);
          const fallback = names.length < 3 ? names.join(" and ") : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
          done.querySelector("[data-ms-done-title]").textContent = `You're on the list, ${data.first_name || first}.`;
          done.querySelector("[data-ms-done-text]").textContent = `We'll email you when ${data.store || fallback} opens.`;
          fields.hidden = true;
          done.hidden = false;
          done.focus();
          f("first_name").value = "";
          f("email").value = "";
          return;
        }
        if (data.errors) {
          Object.entries(data.errors).forEach(([k, v]) => setError(k, v));
          form.querySelector('[aria-invalid="true"]')?.focus();
        } else {
          formError.textContent = data.error || "Something went wrong on our end. Try again in a minute.";
          formError.hidden = false;
        }
      } catch {
        formError.textContent = "Couldn't send that. Check your connection and try again.";
        formError.hidden = false;
      } finally {
        btn.disabled = false;
        btn.textContent = label;
      }
    });
    return { reset, focus: () => f("first_name").focus() };
  }

  function initNotify(dialog) {
    let chosen = [];
    const form = dialog.querySelector("form");
    const ui = bindNotifyForm(form, () => chosen);
    // Delegated, so "Nearest to you" copies of the cards work too.
    document.addEventListener("click", (e) => {
      const b = e.target.closest("[data-ms-notify]");
      if (!b) return;
      chosen = [{ handle: b.dataset.msNotify, name: b.dataset.msNotifyName || "this store" }];
      dialog.querySelector("[data-ms-notify-store]").textContent = chosen[0].name;
      ui.reset();
      dialog.showModal();
      ui.focus();
    });
    dialog.querySelectorAll("[data-ms-notify-close]").forEach((b) => b.addEventListener("click", () => dialog.close()));
    dialog.addEventListener("click", (e) => { if (e.target === dialog) dialog.close(); });
  }

  function initNotifyPage(form) {
    bindNotifyForm(form, () =>
      [...form.querySelectorAll('[name="stores"]')]
        .filter((i) => i.type === "hidden" || i.checked)
        .map((i) => ({ handle: i.value, name: i.dataset.name || i.value })),
    );
  }

  /* ---------- Store page ---------- */
  function initStore(root) {
    renderStatus(root);
    const tz = root.dataset.tz;
    const today = nowIn(tz);
    root.querySelectorAll("[data-ms-date]").forEach((el) => { if (el.dataset.msDate < today.date || el.dataset.msDate > addDays(today.date, 6)) el.remove(); });
    root.querySelectorAll("[data-ms-dow]").forEach((row) => row.classList.toggle("is-today", Number(row.dataset.msDow) === today.day));
    root.querySelectorAll("[data-ms-near-store]").forEach((c) => {
      renderStatus(c);
      const d = km({ lat: +root.dataset.lat, lng: +root.dataset.lng }, { lat: +c.dataset.lat, lng: +c.dataset.lng });
      const out = c.querySelector("[data-ms-distance]");
      if (out && Number.isFinite(d)) { out.textContent = kmText(d); out.hidden = false; }
    });

    const track = root.querySelector("[data-ms-track]");
    if (track) {
      const slides = track.children.length;
      const count = root.querySelector("[data-ms-count]");
      const at = () => Math.round(track.scrollLeft / track.clientWidth);
      const go = (i) => track.scrollTo({ left: ((i + slides) % slides) * track.clientWidth, behavior: "smooth" });
      track.addEventListener("scroll", () => { if (count) count.textContent = `${at() + 1} / ${slides}`; }, { passive: true });
      root.querySelector("[data-ms-prev]")?.addEventListener("click", () => go(at() - 1));
      root.querySelector("[data-ms-next]")?.addEventListener("click", () => go(at() + 1));
    }

    const box = root.querySelector("[data-ms-lightbox]");
    const shots = [...root.querySelectorAll("[data-ms-shot]")];
    if (box && shots.length && box.showModal) {
      const img = box.querySelector("img");
      let i = 0;
      const show = (n) => { i = (n + shots.length) % shots.length; img.src = shots[i].dataset.msShot; img.alt = shots[i].querySelector("img")?.alt || ""; };
      shots.forEach((s, n) => s.addEventListener("click", () => { show(n); box.showModal(); }));
      box.querySelector("[data-ms-lb-prev]").addEventListener("click", () => show(i - 1));
      box.querySelector("[data-ms-lb-next]").addEventListener("click", () => show(i + 1));
      box.querySelector("[data-ms-lb-close]").addEventListener("click", () => box.close());
      box.addEventListener("click", (e) => { if (e.target === box) box.close(); });
      box.addEventListener("keydown", (e) => { if (e.key === "ArrowLeft") show(i - 1); if (e.key === "ArrowRight") show(i + 1); });
    }
  }

  function boot() {
    document.querySelectorAll("[data-ms-index]").forEach(initIndex);
    document.querySelectorAll("[data-ms-suggest]").forEach(initSuggest);
    document.querySelectorAll("[data-ms-store-page]").forEach(initStore);
    document.querySelectorAll("[data-ms-notify-dialog]").forEach(initNotify);
    document.querySelectorAll("[data-ms-notify-form]").forEach(initNotifyPage);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
